import { useEffect, useRef, useState } from "react";
import { useStore } from "../lib/store";

/**
 * Fullscreen-overlay voor een afbeelding. De `url` is een al-geladen
 * blob: URL (AttachmentChip heeft 'm met Bearer-auth opgehaald), dus we
 * tonen 'm direct zonder nieuwe fetch. Sluit op klik buiten de afbeelding,
 * op de ✕-knop, of met Escape.
 *
 * Touch: pinch-zoom met twee vingers, pan met één vinger als ingezoomd.
 * Dubbel-tap zoomt heen-en-weer naar 2×. Op desktop blijft alles bij de
 * standaard fit-to-screen weergave.
 */
export default function Lightbox() {
  const lightbox = useStore((s) => s.lightbox);
  const close = useStore((s) => s.closeLightbox);

  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // Actieve pointers voor pinch-detectie. Map id → { x, y }.
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  // Snapshot van scale+pan + initiële pinch-afstand bij start van gesture.
  const gestureStartRef = useRef<
    | null
    | {
        kind: "pan";
        startTx: number;
        startTy: number;
        x: number;
        y: number;
      }
    | {
        kind: "pinch";
        startScale: number;
        startTx: number;
        startTy: number;
        startDist: number;
        startMid: { x: number; y: number };
      }
  >(null);
  const lastTapRef = useRef<number>(0);

  // Reset bij wisselen van lightbox.
  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
    pointersRef.current.clear();
    gestureStartRef.current = null;
  }, [lightbox?.url]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close]);

  if (!lightbox) return null;

  function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.hypot(dx, dy);
  }
  function mid(a: { x: number; y: number }, b: { x: number; y: number }) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  function onPointerDown(e: React.PointerEvent) {
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    (e.target as Element).setPointerCapture(e.pointerId);
    const pts = [...pointersRef.current.values()];
    if (pts.length === 2) {
      gestureStartRef.current = {
        kind: "pinch",
        startScale: scale,
        startTx: tx,
        startTy: ty,
        startDist: dist(pts[0]!, pts[1]!),
        startMid: mid(pts[0]!, pts[1]!),
      };
    } else if (pts.length === 1) {
      gestureStartRef.current = {
        kind: "pan",
        startTx: tx,
        startTy: ty,
        x: e.clientX,
        y: e.clientY,
      };
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointersRef.current.values()];
    const gs = gestureStartRef.current;
    if (!gs) return;
    if (gs.kind === "pinch" && pts.length === 2) {
      const newDist = dist(pts[0]!, pts[1]!);
      const ratio = newDist / gs.startDist;
      const nextScale = Math.min(6, Math.max(1, gs.startScale * ratio));
      const newMid = mid(pts[0]!, pts[1]!);
      // Pan zodat het midden van de pinch hetzelfde punt op de
      // afbeelding blijft volgen.
      const dx = newMid.x - gs.startMid.x;
      const dy = newMid.y - gs.startMid.y;
      setScale(nextScale);
      setTx(gs.startTx + dx);
      setTy(gs.startTy + dy);
    } else if (gs.kind === "pan" && pts.length === 1 && scale > 1.01) {
      setTx(gs.startTx + (e.clientX - gs.x));
      setTy(gs.startTy + (e.clientY - gs.y));
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size === 0) {
      // Double-tap detectie — alleen op touch, niet op muis.
      if (e.pointerType !== "mouse") {
        const now = Date.now();
        if (now - lastTapRef.current < 300) {
          if (scale > 1.5) {
            setScale(1);
            setTx(0);
            setTy(0);
          } else {
            setScale(2);
          }
          lastTapRef.current = 0;
        } else {
          lastTapRef.current = now;
        }
      }
      gestureStartRef.current = null;
    } else if (pointersRef.current.size === 1) {
      // Tijdens pinch is één vinger losgelaten — schakel naar pan-mode.
      const remaining = [...pointersRef.current.values()][0]!;
      gestureStartRef.current = {
        kind: "pan",
        startTx: tx,
        startTy: ty,
        x: remaining.x,
        y: remaining.y,
      };
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/80 p-4 md:p-8"
      onClick={close}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ touchAction: "none" }}
    >
      <button
        onClick={close}
        aria-label="Sluiten"
        className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-slate-800/80 text-xl text-slate-200 hover:bg-slate-700"
      >
        ✕
      </button>
      <img
        src={lightbox.url}
        alt={lightbox.filename}
        className="max-h-full max-w-full rounded object-contain shadow-2xl"
        draggable={false}
        style={{
          transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
          transformOrigin: "center center",
          transition: gestureStartRef.current ? "none" : "transform 0.15s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-slate-900/80 px-3 py-1 text-xs text-slate-300">
        {lightbox.filename}
      </div>
    </div>
  );
}
